import { useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Collapse,
  Group,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core'
import { BarChart, DonutChart } from '@mantine/charts'
import {
  IconAlertTriangle,
  IconChartBar,
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconTag,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { finopsApi } from '@/api/finops'
import type { FinopsTagCoverageConfidence } from '@/types/finops'
import { WidgetCard, EmptyState } from '@/components/ui'
import { formatUSD } from '@/utils/finops'

type TagKey = 'project' | 'environment'

const DONUT_COLORS = [
  'teal.6',
  'cyan.6',
  'blue.6',
  'indigo.6',
  'violet.6',
  'grape.6',
  'pink.6',
  'orange.6',
  'yellow.6',
  'lime.6',
]

/** Mapeia confidence para cor e rótulo do badge. */
function confidenceBadge(confidence: FinopsTagCoverageConfidence): {
  color: string
  label: string
} {
  switch (confidence) {
    case 'alta':
      return { color: 'green', label: 'Cobertura alta' }
    case 'media':
      return { color: 'yellow', label: 'Cobertura média' }
    case 'baixa':
      return { color: 'red', label: 'Cobertura baixa' }
  }
}

export function ShowbackTab() {
  const [tag, setTag] = useState<TagKey>('project')
  const [search, setSearch] = useState('')
  const [untaggedExpanded, setUntaggedExpanded] = useState(false)

  const reportsQuery = useQuery({
    queryKey: ['finops-reports-showback'],
    queryFn: () => finopsApi.getReports({ limit: 1 }),
    staleTime: 300_000,
  })

  const latestReportId = reportsQuery.data?.[0]?.id ?? null

  const showbackQuery = useQuery({
    queryKey: ['finops-showback', latestReportId, tag],
    queryFn: () => finopsApi.getShowback(latestReportId!, tag, 10),
    enabled: latestReportId != null,
    staleTime: 300_000,
  })

  const untaggedByAccountQuery = useQuery({
    queryKey: ['finops-untagged-by-account', tag],
    queryFn: () => finopsApi.getUntaggedByAccount(undefined, tag),
    enabled: untaggedExpanded,
    staleTime: 300_000,
  })

  const data = showbackQuery.data

  const filteredEntries = useMemo(() => {
    if (!data) return []
    if (!search) return data.entries
    const q = search.toLowerCase()
    return data.entries.filter((e) => e.display_label.toLowerCase().includes(q))
  }, [data, search])

  if (reportsQuery.isLoading || showbackQuery.isLoading) {
    return <Skeleton height={300} />
  }

  if (!latestReportId) {
    return (
      <EmptyState
        icon={IconChartBar}
        title="Sem dados de showback"
        description="Ative cost allocation tags no AWS Billing Console e aguarde 24h de propagação para gerar o primeiro relatório."
        accent="info"
      />
    )
  }

  if (!data || data.entries.length === 0) {
    return (
      <EmptyState
        icon={IconChartBar}
        title={`Sem dados de tag ${tag}`}
        description="Adicione tags de Project/Environment aos recursos AWS (via Console ou IaC) e aguarde a próxima coleta semanal."
        accent="info"
      />
    )
  }

  const badge = data.confidence != null ? confidenceBadge(data.confidence) : null
  const untaggedAccounts = untaggedByAccountQuery.data?.untagged_by_account ?? []
  const untaggedYm = untaggedByAccountQuery.data?.ym

  return (
    <Stack>
      <Group>
        <SegmentedControl
          value={tag}
          onChange={(v) => setTag(v as TagKey)}
          data={[
            { label: 'Project', value: 'project' },
            { label: 'Environment', value: 'environment' },
          ]}
        />
        <Text size="xs" c="dimmed">
          Total: <strong>{formatUSD(data.total_usd)}</strong>
        </Text>
        {badge != null && (
          <Badge color={badge.color} variant="light" leftSection={
            <ThemeIcon color={badge.color} variant="transparent" size="xs">
              <IconTag size={10} />
            </ThemeIcon>
          }>
            {badge.label} ({data.coverage_pct.toFixed(1)}%)
          </Badge>
        )}
      </Group>

      {data.confidence != null && data.confidence !== 'alta' && data.untagged_entry && (
        <Alert
          variant="light"
          color={data.confidence === 'media' ? 'yellow' : 'red'}
          icon={<IconAlertTriangle size={16} />}
          title={
            data.confidence === 'media'
              ? 'Cobertura de tag média'
              : 'Cobertura de tag baixa — atenção!'
          }
        >
          <Text size="sm">
            Recursos sem tag de <strong>{tag}</strong> representam{' '}
            <strong>{data.untagged_entry.pct.toFixed(1)}%</strong> do custo (
            {formatUSD(data.untagged_entry.amount_usd)}
            ). Cobertura atual: <strong>{data.coverage_pct.toFixed(1)}%</strong>.
            Adicionar tags via AWS Console ou IaC reduz o bucket e melhora a precisão do
            rateio.
          </Text>
        </Alert>
      )}

      {data.possible_duplicates.length > 0 && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle size={16} />}
          title="Possíveis duplicatas detectadas"
        >
          <Stack gap={4}>
            {data.possible_duplicates.map((pair, i) => (
              <Text key={i} size="sm">
                {pair.join(' ⇄ ')}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Seção colapsável: Custo sem tag por conta (CE ao vivo) */}
      {data.untagged_entry && (
        <WidgetCard p="md">
          <UnstyledButton
            onClick={() => setUntaggedExpanded((v) => !v)}
            w="100%"
          >
            <Group justify="space-between">
              <Group gap="xs">
                <IconTag size={16} color="var(--mantine-color-yellow-6)" />
                <Text size="sm" fw={600}>
                  Custo sem tag por conta
                </Text>
                <Text size="xs" c="dimmed">
                  (mês corrente, ao vivo)
                </Text>
              </Group>
              {untaggedExpanded ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </Group>
          </UnstyledButton>

          <Collapse in={untaggedExpanded}>
            <Stack mt="sm" gap="sm">
              {untaggedByAccountQuery.isLoading && <Skeleton height={80} />}

              {!untaggedByAccountQuery.isLoading && untaggedAccounts.length === 0 && (
                <Text size="sm" c="dimmed">
                  Nenhuma conta com custo sem tag encontrada
                  {untaggedYm ? ` em ${untaggedYm}` : ''}.
                </Text>
              )}

              {untaggedAccounts.length > 0 && (
                <>
                  <Text size="xs" c="dimmed">
                    Período:{' '}
                    <strong>{untaggedYm ?? 'mês corrente'}</strong> — dados ao
                    vivo do Cost Explorer
                  </Text>
                  <Table.ScrollContainer minWidth={400}>
                    <Table striped>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Conta AWS</Table.Th>
                          <Table.Th>Custo sem tag</Table.Th>
                          <Table.Th>% do custo sem tag</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {untaggedAccounts.map((item) => (
                          <Table.Tr key={item.account_id}>
                            <Table.Td>
                              <Text size="sm" ff="monospace">
                                {item.account_id}
                              </Text>
                            </Table.Td>
                            <Table.Td>{formatUSD(item.amount_usd)}</Table.Td>
                            <Table.Td>{item.pct.toFixed(2)}%</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                  <Alert
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle size={14} />}
                  >
                    <Text size="xs">
                      Para corrigir: adicione a tag{' '}
                      <strong>{data.tag_key}</strong> nos recursos das contas
                      acima via <strong>IaC (Terraform/CDK)</strong> ou AWS
                      Console. Após a próxima coleta semanal a cobertura será
                      recalculada.
                    </Text>
                  </Alert>
                </>
              )}
            </Stack>
          </Collapse>
        </WidgetCard>
      )}

      <WidgetCard p="md">
        <Text size="sm" fw={600} mb="sm">{`Custo por ${tag} (último mês)`}</Text>
        {tag === 'environment' ? (
          <Box visibleFrom="sm">
            <DonutChart
              h={240}
              data={data.top_entries.map((e, idx) => ({
                name: e.display_label,
                value: e.amount_usd,
                color: DONUT_COLORS[idx % DONUT_COLORS.length],
              }))}
              valueFormatter={(v) => formatUSD(v)}
              withLabels
              withTooltip
            />
          </Box>
        ) : (
          <BarChart
            h={280}
            data={data.top_entries.slice(0, 10).map((e) => ({
              tag: e.display_label,
              total: e.amount_usd,
            }))}
            dataKey="tag"
            orientation="vertical"
            yAxisProps={{ width: 180 }}
            series={[{ name: 'total', color: 'teal.6', label: 'Custo' }]}
            valueFormatter={(v) => formatUSD(v)}
          />
        )}
        {data.other_entries_count > 0 && (
          <Text c="dimmed" size="sm" mt="sm">
            + Outros ({data.other_entries_count}{' '}
            {data.other_entries_count === 1 ? 'tag' : 'tags'})
          </Text>
        )}
      </WidgetCard>

      <WidgetCard p="md">
        <Text size="sm" fw={600} mb="sm">
          Todos os valores
        </Text>
        <TextInput
          placeholder="Filtrar por nome..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          mb="sm"
          size="xs"
        />
        <Table.ScrollContainer minWidth={400}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{tag === 'project' ? 'Project' : 'Environment'}</Table.Th>
                <Table.Th>Custo</Table.Th>
                <Table.Th>%</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredEntries.map((e) => (
                <Table.Tr key={e.key}>
                  <Table.Td>{e.display_label}</Table.Td>
                  <Table.Td>{formatUSD(e.amount_usd)}</Table.Td>
                  <Table.Td>{e.pct.toFixed(2)}%</Table.Td>
                </Table.Tr>
              ))}
              {data.untagged_entry && !search && (
                <Table.Tr key="untagged" bg="yellow.0">
                  <Table.Td>
                    <Text c="yellow.7" fw={500}>
                      {data.untagged_entry.display_label}
                    </Text>
                  </Table.Td>
                  <Table.Td>{formatUSD(data.untagged_entry.amount_usd)}</Table.Td>
                  <Table.Td>{data.untagged_entry.pct.toFixed(2)}%</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </WidgetCard>
    </Stack>
  )
}
